import { createEffect, DEV, onCleanup } from "solid-js"
import { DataTexture, ObjectSpaceNormalMap, Texture, WebGLRenderTarget, type MeshLambertMaterial } from "three"
import { onCleanupDispose } from "../../../../../../rx/cleanup/onCleanupDispose"
import { useIsEngineActiveMemo } from "../../../../../../rx/memo/useIsEngineActiveMemo"
import type { TextureTypeName } from "../../../../../../typescript"
import type { Engine } from "../../../../../core/engine/Engine.type"
import type { TileGraphNode } from "../../../../../core/engine/TileGraphNode/TileGraphNode.type"
import type { QuadrantBound } from "../../../../../Quadtree/createEntityQuadtreeEffect"
import useEngineTextureWriter from "../../../../fn/useEngineTextureWriter"
import createFourByFourToMergedTextureShader from "../../../../texture/FourByFourToMergedTexture/createFourByFourToMergedTextureMRTShader"
import type { TextureSizeSettingId } from "./TextureSizeSettingId.enum"

export default function createGeneric4x4TextureMergeEffect(
  engine: Engine,
  quadrant: QuadrantBound,
  material: MeshLambertMaterial,
  materialList: Array<MeshLambertMaterial>,
  trackMaterialList: () => void,
  textureTypeNameList: Array<TextureTypeName<MeshLambertMaterial>>,
  textureSize: TextureSizeSettingId,
) {
  if (DEV) {
    if (!(textureTypeNameList.length > 0)) {
      throw new Error('createTextureMergeEffect: textureTypeNameList.length > 0')
    }
  }
  const [getTextureWriter] = useEngineTextureWriter(engine)
  const textureWriter = getTextureWriter()
  const blankTexture = new Texture()
  const blankAOTexture = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
  blankAOTexture.needsUpdate = true
  onCleanupDispose(blankAOTexture)
  const shaderMaterial = createFourByFourToMergedTextureShader()
  onCleanupDispose(shaderMaterial)

  const targetTextureList: Array<Texture> = shaderMaterial.uniforms.u_textures.value as Array<Texture>
  if (DEV) {
    console.assert(targetTextureList instanceof Array, 'targetTextureList instanceof Array')
  }
  const renderTargetMap = new Map<TextureTypeName<MeshLambertMaterial>, WebGLRenderTarget>()
  onCleanup(() => {
    renderTargetMap.forEach(renderTarget => renderTarget.dispose())
    renderTargetMap.clear()
  })

  const isEngineActive = useIsEngineActiveMemo(engine)
  createEffect(() => {
    if (isEngineActive()) {
      createEffect(() => {
        trackMaterialList()
        targetTextureList.length = 16

        for (let textureTypeNameIndex = 0; textureTypeNameIndex < textureTypeNameList.length; textureTypeNameIndex++) {
          const textureTypeName = textureTypeNameList[textureTypeNameIndex]
          // Initialize texture array with blank textures (aoMap should default to white)
          const fallbackTexture = textureTypeName === 'aoMap' ? blankAOTexture : blankTexture
          targetTextureList.fill(fallbackTexture)
          const targetTexture = material[textureTypeName]
          if (targetTexture) {
            let renderTarget = renderTargetMap.get(textureTypeName)
            if (renderTarget && (renderTarget.width !== textureSize || renderTarget.height !== textureSize)) {
              renderTarget.dispose()
              renderTarget = void 0
              renderTargetMap.delete(textureTypeName)
            }
            if (!renderTarget) {
              renderTarget = new WebGLRenderTarget(textureSize, textureSize)
            }
            renderTargetMap.set(textureTypeName, renderTarget)
            // Fill texture array with diffuse textures
            for (let i = 0; i < materialList.length; i++) {
              const material = materialList[i]
              const inputTexture = material[textureTypeName]
              const node = material.userData.node as TileGraphNode | null
              if (node && inputTexture) {
                const { x, y } = node
                const textureArrayIndex = x - quadrant.x + ((y - quadrant.y) * quadrant.width)
                targetTextureList[textureArrayIndex] = inputTexture
              }
            }
            
            if (DEV) {
              console.assert(targetTextureList.length === 16)
            }

            // Render pass: merge textures for the current map type
            textureWriter.render(shaderMaterial, renderTarget)
            const mergedTargetTexture = renderTarget.texture
            mergedTargetTexture.needsUpdate = true
            material[textureTypeName] = mergedTargetTexture

            // this probably belongs outside of this function
            if (textureTypeName === 'normalMap') {
              material.normalMapType = ObjectSpaceNormalMap
            }
            // targetTexture.needsUpdate = true
          }
        }
        
        material.needsUpdate = true
      })

    }
  })
}
